import type { ImageArea } from "@/lib/types";

export const PRINT_DPI = 300;
export const LOW_RESOLUTION_WARNING_DPI = 150;
export const MAX_PRINT_PIXELS = 60_000_000;
export const MAX_PRINT_FILE_BYTES = 40 * 1024 * 1024;

type PrintArea = Pick<
  ImageArea,
  | "widthCm"
  | "heightCm"
  | "wrapAllowanceCm"
  | "fillAllowanceCm"
>;

export function getAreaAspectRatio(area: Pick<ImageArea, "widthCm" | "heightCm">) {
  return area.widthCm / area.heightCm;
}

export function getCropAreaSize(area: PrintArea) {
  return {
    widthCm: area.widthCm + area.wrapAllowanceCm * 2,
    heightCm: area.heightCm + area.wrapAllowanceCm * 2,
  };
}

export function getPrintAreaSize(area: PrintArea) {
  const cropArea = getCropAreaSize(area);
  return {
    widthCm: cropArea.widthCm + area.fillAllowanceCm * 2,
    heightCm: cropArea.heightCm + area.fillAllowanceCm * 2,
  };
}

export function getCropAreaAspectRatio(area: PrintArea) {
  const size = getCropAreaSize(area);
  return size.widthCm / size.heightCm;
}

export function getCropAreaPixelDimensions(area: PrintArea) {
  const size = getCropAreaSize(area);
  return getPrintPixelDimensions(size.widthCm, size.heightCm);
}

export function getPrintAreaPixelDimensions(area: PrintArea) {
  const size = getPrintAreaSize(area);
  return getPrintPixelDimensions(size.widthCm, size.heightCm);
}

export function getFaceWindow(
  area: PrintArea,
  printWindow: { left: number; top: number; width: number; height: number },
) {
  const cropArea = getCropAreaSize(area);
  const width = area.widthCm / cropArea.widthCm;
  const height = area.heightCm / cropArea.heightCm;
  return {
    left:
      printWindow.left +
      (area.wrapAllowanceCm / cropArea.widthCm) * printWindow.width,
    top:
      printWindow.top +
      (area.wrapAllowanceCm / cropArea.heightCm) * printWindow.height,
    width: printWindow.width * width,
    height: printWindow.height * height,
  };
}

export function getPrintPixelDimensions(
  widthCm: number,
  heightCm: number,
) {
  const width = Math.round((widthCm / 2.54) * PRINT_DPI);
  const height = Math.round((heightCm / 2.54) * PRINT_DPI);
  if (width < 1 || height < 1 || width * height > MAX_PRINT_PIXELS) {
    throw new Error(
      "El tamaño de impresión es demasiado grande para prepararlo. Reduce las dimensiones del área.",
    );
  }
  return { width, height };
}

export function getCropResolution(
  sourceWidth: number,
  sourceHeight: number,
  cropWidth: number,
  cropHeight: number,
  area: PrintArea,
) {
  const size = getCropAreaSize(area);
  const width = Math.max(1, Math.round(sourceWidth * cropWidth));
  const height = Math.max(1, Math.round(sourceHeight * cropHeight));
  const dpi = Math.min(
    (width / size.widthCm) * 2.54,
    (height / size.heightCm) * 2.54,
  );
  return { width, height, dpi };
}

function pngCrc32(bytes: Uint8Array) {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

export async function addPrintResolutionMetadata(blob: Blob) {
  const png = new Uint8Array(await blob.arrayBuffer());
  const header = new DataView(png.buffer, png.byteOffset, png.byteLength);
  const isPng =
    png.length >= 33 &&
    png[0] === 0x89 &&
    png[1] === 0x50 &&
    png[2] === 0x4e &&
    png[3] === 0x47 &&
    png[4] === 0x0d &&
    png[5] === 0x0a &&
    png[6] === 0x1a &&
    png[7] === 0x0a &&
    String.fromCharCode(...png.slice(12, 16)) === "IHDR" &&
    header.getUint32(8) === 13;
  if (!isPng) {
    throw new Error("No se pudo preparar la resolución de impresión.");
  }

  const ihdrEnd = 8 + 12 + header.getUint32(8);
  const chunk = new Uint8Array(21);
  const chunkView = new DataView(chunk.buffer);
  chunkView.setUint32(0, 9);
  chunk.set([0x70, 0x48, 0x59, 0x73], 4);
  const pixelsPerMeter = Math.round(PRINT_DPI / 0.0254);
  chunkView.setUint32(8, pixelsPerMeter);
  chunkView.setUint32(12, pixelsPerMeter);
  chunk[16] = 1;
  chunkView.setUint32(17, pngCrc32(chunk.subarray(4, 17)));

  const segments = [png.subarray(0, ihdrEnd), chunk];
  let offset = ihdrEnd;
  let foundEnd = false;
  while (offset < png.length) {
    if (offset + 12 > png.length) {
      throw new Error("El archivo PNG está incompleto.");
    }
    const length = header.getUint32(offset);
    const end = offset + 12 + length;
    if (end > png.length) {
      throw new Error("El archivo PNG está incompleto.");
    }
    const type = String.fromCharCode(...png.subarray(offset + 4, offset + 8));
    if (type !== "pHYs") {
      segments.push(png.subarray(offset, end));
    }
    offset = end;
    if (type === "IEND") {
      foundEnd = true;
      break;
    }
  }
  if (!foundEnd) {
    throw new Error("El archivo PNG está incompleto.");
  }
  if (offset < png.length) {
    segments.push(png.subarray(offset));
  }

  const output = new Uint8Array(
    segments.reduce((length, segment) => length + segment.length, 0),
  );
  let outputOffset = 0;
  for (const segment of segments) {
    output.set(segment, outputOffset);
    outputOffset += segment.length;
  }
  return new Blob([output], { type: "image/png" });
}
